import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import type { PortfolioRead } from '../types';

/**
 * `usePortfolios` — TanStack Query hook fetching the org-scoped portfolios
 * list (Core F13h `GET /api/v1/portfolios`).
 *
 * Senviro v2 is a thin UX layer over Core: this hook calls the apps/api proxy
 * (`/api/v1/proxy/core/portfolios`), which uses `@senviro/core-api-client` to
 * forward to Core with the bearer token + 3-tier tenant context. NO
 * Senviro-side Prisma model is involved.
 */
export const PORTFOLIOS_QUERY_KEY = ['portfolios'] as const;

export interface PortfoliosListResponse {
  items: PortfolioRead[];
  total: number;
}

export function usePortfolios(): UseQueryResult<PortfoliosListResponse> {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: PORTFOLIOS_QUERY_KEY,
    queryFn: async (): Promise<PortfoliosListResponse> => {
      const data = await apiFetch<PortfoliosListResponse | PortfolioRead[]>(
        '/api/v1/proxy/core/portfolios',
        { getAccessToken },
      );
      // Tolerate both `{ items, total }` (Core paginated) and bare-array shapes
      // since Core ADR 0024 is gradually moving to unwrapped lists.
      if (Array.isArray(data)) {
        return { items: data, total: data.length };
      }
      return data;
    },
  });
}
